/**
 * 音频/视频播放结束回调逻辑（抽离自 MusicPlayer.js）
 * 供 useEffect 中 attach 到 mediaElement 的 'ended' 事件
 */
import { getNextTrackAfterEnd, getEndedAction, ENDED_ACTION_SWITCH_NEXT, ENDED_ACTION_STOP } from './playbackEnd';
import { runFadeIn } from './fadeUtils';

/**
 * 创建 handleEnded 回调
 * @param {object} params
 */
export function createHandleEnded({
  mediaElement,
  currentMusic,
  duration,
  playlist,
  playMode,
  playlistLoop,
  hasVideo,
  fadeInOut,
  isSingleCoverMode,
  muted,
  volume,
  videoRef,
  audioRef,
  coverFadeOutStartedRef,
  coverActiveLayerRef,
  getUserId,
  axios,
  stopProgressTracking,
  stopPlayHistoryTracking,
  updatePlaying,
  setContextPlaying,
  setShowLyrics,
  handleNext,
  startProgressTracking,
  startPlayHistoryTracking,
  setCoverLayerAOpacity,
  setIsVisualSwitching,
  setVisualFadeInToken
}) {
  return function handleEnded() {
    stopProgressTracking();
    stopPlayHistoryTracking();
    if (mediaElement && !mediaElement.paused) mediaElement.pause();

    if (currentMusic) {
      const userId = getUserId();
      axios.post(`/api/music/${currentMusic.id}/play`, {
        position: duration,
        duration,
        completed: 1
      }, { headers: { 'X-User-ID': userId } }).catch(console.error);
    }

    const currentIndex = playlist && playlist.length > 0 ? playlist.findIndex(m => m.id === currentMusic?.id) : -1;
    const nextResult = getNextTrackAfterEnd(playMode, playlistLoop, playlist, currentIndex);
    const action = getEndedAction(playMode, playlist, currentMusic, nextResult);

    if (action === ENDED_ACTION_STOP) {
      if (mediaElement) {
        try {
          mediaElement.pause();
          mediaElement.currentTime = mediaElement.duration || 0;
        } catch (_) {}
      }
      updatePlaying(false);
      setShowLyrics(false);
      return;
    }

    if (action === ENDED_ACTION_SWITCH_NEXT) {
      handleNext();
      setTimeout(() => {
        updatePlaying(true);
        setContextPlaying(true);
      }, 0);
      return;
    }

    // ENDED_ACTION_REPLAY_SAME
    if (playMode === 'repeat') {
      mediaElement.currentTime = 0;
      const playPromise = mediaElement.play();
      if (playPromise !== undefined) {
        playPromise.catch(err => {
          updatePlaying(false);
          console.error('Repeat play failed:', err);
        });
      }
      updatePlaying(true);
      startProgressTracking();
      startPlayHistoryTracking();
      return;
    }

    // 同首重播
    if (mediaElement) {
      try {
        coverFadeOutStartedRef.current = false;
        mediaElement.currentTime = 0;
        if (fadeInOut) {
          if (hasVideo && videoRef.current) videoRef.current.volume = 0;
          else if (audioRef.current) audioRef.current.volume = 0;
          if (isSingleCoverMode) {
            coverActiveLayerRef.current = 'A';
            setCoverLayerAOpacity(0);
          }
        }
        const playPromise = mediaElement.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              updatePlaying(true);
              startProgressTracking();
              startPlayHistoryTracking();
              if (fadeInOut) {
                setIsVisualSwitching(false);
                setVisualFadeInToken((v) => v + 1);
                const targetVolume = muted ? 0 : volume;
                runFadeIn(mediaElement, targetVolume);
              }
            })
            .catch(err => {
              updatePlaying(false);
              console.error('Replay failed:', err);
            });
        }
      } catch (err) {
        updatePlaying(false);
        console.error('Replay failed:', err);
      }
    }
  };
}
